import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { OrdersService } from '../../services/orders.service';

export interface PostingOverrideValue {
	gen_prod_posting_group: string;
	note: string;
}

interface PostingGroupOption {
	code: string;
	description: string | null;
	sales_account: string | null;
	cogs_account: string | null;
	cogs_account_interim: string | null;
}

@Component({
	selector: 'app-order-posting-override-button',
	templateUrl: './order-posting-override-button.component.html',
	styleUrls: ['./order-posting-override-button.component.scss'],
})
export class OrderPostingOverrideButtonComponent implements OnInit, OnChanges, OnDestroy {
	@Input() orderno: string | number | null = null;
	@Input() disabled = false;
	@Input() compact = false;
	/** When order has no number yet, parent keeps staged selection across destroy/recreate (e.g. Bill To edit panel). */
	@Input() stagedFromParent: PostingOverrideValue | null = null;

	@Output() overrideChanged = new EventEmitter<PostingOverrideValue | null>();

	@ViewChild('postingOverrideModal') modalRef: ElementRef;

	loading = false;
	saving = false;
	groupsLoading = false;
	loadError: string | null = null;
	saveError: string | null = null;

	groups: PostingGroupOption[] = [];
	filteredGroups: PostingGroupOption[] = [];
	groupSearch = '';

	current: (PostingOverrideValue & { group?: PostingGroupOption | null; updated_at?: string; updated_by?: string }) | null = null;
	staged: PostingOverrideValue | null = null;

	draftCode = '';
	draftNote = '';

	private modal: NgbModalRef | null = null;
	private destroyed$ = new Subject<void>();

	constructor(private orderService: OrdersService, private modalService: NgbModal) {}

	ngOnInit(): void {
		this.applyStagedFromParent();
		this.refreshOverride();
	}

	private applyStagedFromParent(): void {
		if (this.hasOrderNo) {
			return;
		}
		if (this.stagedFromParent && String(this.stagedFromParent.gen_prod_posting_group || '').trim() !== '') {
			this.staged = {
				gen_prod_posting_group: this.stagedFromParent.gen_prod_posting_group,
				note: this.stagedFromParent.note || '',
			};
		} else {
			this.staged = null;
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['stagedFromParent']) {
			this.applyStagedFromParent();
		}
		if (changes['orderno'] && !changes['orderno'].firstChange) {
			this.refreshOverride();
		}
	}

	ngOnDestroy(): void {
		this.destroyed$.next();
		this.destroyed$.complete();
		if (this.modal) {
			this.modal.dismiss('component-destroyed');
		}
	}

	get hasOrderNo(): boolean {
		const n = this.toOrderNo(this.orderno);
		return n > 0;
	}

	get effectiveOverride(): PostingOverrideValue | null {
		if (this.current) {
			return { gen_prod_posting_group: this.current.gen_prod_posting_group, note: this.current.note || '' };
		}
		return this.staged;
	}

	get badgeLabel(): string {
		const eff = this.effectiveOverride;
		if (!eff) return '';
		return eff.gen_prod_posting_group;
	}

	get tooltipLabel(): string {
		const eff = this.effectiveOverride;
		if (!eff) {
			return this.hasOrderNo
				? 'Change posting for this order.'
				: 'Saved with the order when you create it.';
		}
		const grp = this.current?.group;
		if (grp && grp.description) {
			return eff.gen_prod_posting_group + ': ' + grp.description;
		}
		return 'Posting: ' + eff.gen_prod_posting_group;
	}

	open(): void {
		if (this.disabled) return;
		this.saveError = null;
		this.loadError = null;
		const eff = this.effectiveOverride;
		this.draftCode = eff ? eff.gen_prod_posting_group : '';
		this.draftNote = eff ? eff.note || '' : '';
		this.groupSearch = '';
		this.loadGroups();
		this.modal = this.modalService.open(this.modalRef, {
			ariaLabelledBy: 'posting-override-title',
			backdrop: 'static',
			keyboard: true,
			size: 'lg',
			windowClass: 'posting-override-modal',
		});
		this.modal.result.then(
			() => { this.modal = null; },
			() => { this.modal = null; },
		);
	}

	close(): void {
		if (this.modal) {
			this.modal.dismiss('user-close');
			this.modal = null;
		}
	}

	selectGroup(option: PostingGroupOption): void {
		this.draftCode = option.code;
	}

	trackGroup = (_: number, item: PostingGroupOption): string => item.code;

	filterGroups(): void {
		const term = (this.groupSearch || '').trim().toLowerCase();
		if (!term) {
			this.filteredGroups = this.groups.slice(0, 100);
			return;
		}
		this.filteredGroups = this.groups
			.filter((g) => {
				const code = (g.code || '').toLowerCase();
				const desc = (g.description || '').toLowerCase();
				const sales = (g.sales_account || '').toLowerCase();
				return code.includes(term) || desc.includes(term) || sales.includes(term);
			})
			.slice(0, 100);
	}

	save(): void {
		const code = (this.draftCode || '').trim();
		if (!code) {
			this.saveError = 'Select a group first.';
			return;
		}
		const note = (this.draftNote || '').trim();
		const value: PostingOverrideValue = { gen_prod_posting_group: code, note };

		if (!this.hasOrderNo) {
			this.staged = value;
			this.current = null;
			this.overrideChanged.emit(value);
			this.close();
			return;
		}

		this.saving = true;
		this.saveError = null;
		this.orderService
			.setPostingOverride({ orderno: this.toOrderNo(this.orderno), gen_prod_posting_group: code, note })
			.pipe(takeUntil(this.destroyed$))
			.subscribe({
				next: (res: any) => {
					this.saving = false;
					if (res?.success) {
						this.applyOverrideRow(res.override);
						this.staged = null;
						this.overrideChanged.emit(this.effectiveOverride);
						this.close();
					} else {
						this.saveError = res?.error || 'Could not save.';
					}
				},
				error: (err: any) => {
					this.saving = false;
					this.saveError = this.extractError(err) || 'Could not save.';
				},
			});
	}

	clear(): void {
		if (!this.hasOrderNo) {
			this.staged = null;
			this.current = null;
			this.overrideChanged.emit(null);
			this.close();
			return;
		}

		this.saving = true;
		this.saveError = null;
		this.orderService
			.clearPostingOverride(this.toOrderNo(this.orderno))
			.pipe(takeUntil(this.destroyed$))
			.subscribe({
				next: (res: any) => {
					this.saving = false;
					if (res?.success) {
						this.current = null;
						this.staged = null;
						this.overrideChanged.emit(null);
						this.close();
					} else {
						this.saveError = res?.error || 'Could not clear.';
					}
				},
				error: (err: any) => {
					this.saving = false;
					this.saveError = this.extractError(err) || 'Could not clear.';
				},
			});
	}

	persistStagedOverride(orderno: string | number): Promise<boolean> {
		const ono = this.toOrderNo(orderno);
		if (ono <= 0 || !this.staged) {
			return Promise.resolve(false);
		}
		const value = this.staged;
		return new Promise<boolean>((resolve) => {
			this.orderService
				.setPostingOverride({ orderno: ono, gen_prod_posting_group: value.gen_prod_posting_group, note: value.note })
				.pipe(takeUntil(this.destroyed$))
				.subscribe({
					next: (res: any) => {
						if (res?.success) {
							this.applyOverrideRow(res.override);
							this.staged = null;
							this.overrideChanged.emit(this.effectiveOverride);
							resolve(true);
						} else {
							resolve(false);
						}
					},
					error: () => resolve(false),
				});
		});
	}

	private refreshOverride(): void {
		const ono = this.toOrderNo(this.orderno);
		if (ono <= 0) {
			this.current = null;
			return;
		}
		this.loading = true;
		this.loadError = null;
		this.orderService
			.getPostingOverride(ono)
			.pipe(takeUntil(this.destroyed$))
			.subscribe({
				next: (res: any) => {
					this.loading = false;
					if (res?.success) {
						if (this.hasOrderNo) {
							this.staged = null;
						}
						this.applyOverrideRow(res.override);
					} else {
						this.loadError = res?.error || 'Failed to load posting override.';
					}
				},
				error: (err: any) => {
					this.loading = false;
					this.loadError = this.extractError(err) || 'Failed to load posting override.';
				},
			});
	}

	private loadGroups(): void {
		if (this.groups.length > 0) {
			this.filterGroups();
			return;
		}
		this.groupsLoading = true;
		this.orderService
			.getProductPostingGroups()
			.pipe(takeUntil(this.destroyed$))
			.subscribe({
				next: (res: any) => {
					this.groupsLoading = false;
					this.groups = Array.isArray(res?.groups) ? res.groups : [];
					this.filterGroups();
				},
				error: () => {
					this.groupsLoading = false;
					this.groups = [];
					this.filteredGroups = [];
				},
			});
	}

	private applyOverrideRow(row: any): void {
		if (!row) {
			this.current = null;
			return;
		}
		this.current = {
			gen_prod_posting_group: row.gen_prod_posting_group || '',
			note: row.note || '',
			group: row.group || null,
			updated_at: row.updated_at || row.created_at || '',
			updated_by: row.updated_by || row.created_by || '',
		};
	}

	private toOrderNo(value: string | number | null | undefined): number {
		if (value === null || value === undefined || value === '') return 0;
		const n = parseInt(String(value), 10);
		return isNaN(n) ? 0 : n;
	}

	private extractError(err: any): string {
		if (!err) return '';
		if (typeof err === 'string') return err;
		if (err.error?.error) return err.error.error;
		if (err.error?.message) return err.error.message;
		if (err.message) return err.message;
		return '';
	}
}
